import { useEffect, useMemo, useState } from "react";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";
import Page36_TabelHistoryBan from "./Page36_TabelHistoryBan";

const toDateOnly = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatNum = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("id-ID");
};

const moveUserToLast = (cols) => {
  const arr = Array.isArray(cols) ? cols : [];
  const withoutUser = arr.filter((col) => String(col || "").toLowerCase() !== "user");
  return arr.some((col) => String(col || "").toLowerCase() === "user")
    ? [...withoutUser, "user"]
    : withoutUser;
};

export function Page35_TabelHistoryMT({
  open,
  onClose,
  initialKeyword = "",
  opsiCabang = [],
  globalOpsi = {},
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [info, setInfo] = useState({ id_mt: "", nopol: "" });
  const [errorText, setErrorText] = useState("");
  const [openHistoryBanModal, setOpenHistoryBanModal] = useState(false);
  const [historyBanKeyword, setHistoryBanKeyword] = useState("");

  const fetchRows = async (rawKeyword) => {
    const cleaned = String(rawKeyword || "").trim();
    if (!cleaned) return;
    setIsLoading(true);
    setErrorText("");
    try {
      const { data } = await api.get(`${backendPort}/api/main/load_mt_history_event_modal`, {
        params: { keyword: cleaned },
        timeout: 120000,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      const firstRow = Array.isArray(data?.rows) && data.rows.length > 0 ? data.rows[0] : {};
      setInfo({
        id_mt: String(data?.id_mt || firstRow?.id_mt || ""),
        nopol: String(firstRow?.nopol || ""),
      });
    } catch (err) {
      setRows([]);
      setInfo({ id_mt: "", nopol: "" });
      setErrorText(err?.response?.data?.message || "Gagal memuat history MT");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const first = String(initialKeyword || "").trim();
    if (!first) return;
    fetchRows(first);
  }, [open, initialKeyword]);

  const columns = useMemo(() => {
    const cabangMap = new Map(
      (Array.isArray(opsiCabang) ? opsiCabang : []).map((x) => [
        String(x?.kode_cabang || "").trim(),
        String(x?.nama_cabang || "").trim() || String(x?.kode_cabang || "").trim(),
      ])
    );
    const set = new Set();
    const transformedRows = (Array.isArray(rows) ? rows : []).map((row) => {
      const next = { ...(row || {}) };
      const kode = String(next?.kode_cabang || "").trim();
      next.cabang = cabangMap.get(kode) || kode || "-";
      delete next.kode_cabang;
      delete next.id_mt;
      delete next.nopol;
      return next;
    });
    transformedRows.forEach((row) => {
      Object.keys(row || {}).forEach((k) => set.add(k));
    });
    const priority = [
      "tgl",
      "event_mt",
      "cabang",
      "odo",
      "kmt",
      "status_mt",
      "id_ban_terkait",
    ];
    const dynamic = Array.from(set);
    const head = priority.filter((x) => dynamic.includes(x));
    const tail = dynamic.filter((x) => !priority.includes(x));
    return {
      rows: transformedRows,
      cols: moveUserToLast([...head, ...tail]),
    };
  }, [rows, opsiCabang]);

  if (!open) return null;

  const downloadExcel = () => {
    const headers = ["no", ...columns.cols];
    const escapeHtml = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;");
    const headHtml = headers
      .map((h) => `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeHtml(String(h).replace(/_/g, " ").toUpperCase())}</th>`)
      .join("");
    const bodyHtml = columns.rows
      .map((row, idx) => {
        const rowCells = [
          `<td>${idx + 1}</td>`,
          ...columns.cols.map((col) => {
            const raw = row?.[col];
            const value = col === "tgl"
              ? toDateOnly(raw)
              : (col === "odo" || col === "kmt")
                ? formatNum(raw)
                : String(raw ?? "-");
            const align = col === "odo" || col === "kmt" ? "text-align:right;padding-right:20px;" : "";
            return `<td style="${align}">${escapeHtml(value || "-")}</td>`;
          }),
        ];
        return `<tr>${rowCells.join("")}</tr>`;
      })
      .join("");
    const html = `\uFEFF<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `history_event_mt_${String(info.id_mt || "unknown")}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="modal-backdrop-custom"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="modal-dialog-custom">
        <div className="modal-content-custom" style={{ width: "1100px", height: "80vh" }}>
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">History Event per MT</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2 d-flex justify-content-between align-items-start">
              <span style={{ fontWeight: 700, color: "#7a1f1f" }}>
                {`DATA HISTORY EVENT MT ${info.id_mt || "-"} | Nopol.${info.nopol || "-"}`}
              </span>
              <div className="d-flex align-items-center gap-2">
                {!!errorText && <small className="text-danger">{errorText}</small>}
                <button type="button" className="btn btn-success btn-sm align-self-start" onClick={downloadExcel}>
                  Download
                </button>
                <button type="button" className="btn btn-secondary btn-sm align-self-start" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>

            <div className="border rounded" style={{ maxHeight: "61vh", overflow: "auto" }}>
        <table className="table table-sm mb-0">
          <thead style={{ position: "sticky", top: 0, background: "#0f2d5c", color: "#fff", zIndex: 2 }}>
            <tr>
              <th style={{ whiteSpace: "nowrap", minWidth: 60, background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}>No</th>
                    {columns.cols.map((col) => (
                <th
                  key={col}
                  style={{ whiteSpace: "nowrap", minWidth: 140, background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}
                >
                  {String(col || "").replace(/_/g, " ").toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={Math.max(1, columns.cols.length + 1)} className="text-center text-muted">
                  {isLoading ? "Memuat data..." : "Tidak ada data."}
                </td>
              </tr>
            )}
                  {columns.rows.map((row, idx) => (
                    <tr key={`row-${idx}`}>
                      <td>{idx + 1}</td>
                      {columns.cols.map((col) => {
                        const raw = row?.[col];
                  const value = col === "tgl"
                    ? toDateOnly(raw)
                    : (col === "odo" || col === "kmt")
                      ? formatNum(raw)
                      : String(raw ?? "-");
                        const isIdBanTerkaitCol = String(col || "").toLowerCase() === "id_ban_terkait";
                        const banTokens = isIdBanTerkaitCol
                          ? String(raw ?? "")
                            .split("|")
                            .map((x) => String(x || "").trim())
                            .filter((x) => /^\d+$/.test(x))
                          : [];
                        return (
                          <td
                            key={`${idx}-${col}`}
                            className={col === "odo" || col === "kmt" ? "text-end" : ""}
                            style={{ whiteSpace: "nowrap", paddingRight: col === "odo" || col === "kmt" ? "20px" : undefined }}
                          >
                            {isIdBanTerkaitCol && banTokens.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {banTokens.map((idBan) => (
                                  <button
                                    key={`mt-hist-${idx}-${idBan}`}
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    style={{ color: "#0d6efd", textDecoration: "underline" }}
                                    onClick={() => {
                                      setHistoryBanKeyword(idBan);
                                      setOpenHistoryBanModal(true);
                                    }}
                                  >
                                    {idBan}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              value || "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
          </tbody>
        </table>
      </div>
          </div>
        </div>
      </div>
      <Page36_TabelHistoryBan
        open={openHistoryBanModal}
        onClose={() => setOpenHistoryBanModal(false)}
        initialKeyword={historyBanKeyword}
        opsiCabang={opsiCabang}
        globalOpsi={globalOpsi}
      />
    </div>
  );
}

export default Page35_TabelHistoryMT;
